
import { Button, Modal, Table, Input, DatePicker } from "antd";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import userActions from "../../redux/transactions/actions";
import { RiCodeSLine, RiCodeSSlashLine } from "react-icons/ri";
import { JSONTree } from "react-json-tree";
import { CalendarOutlined } from "@ant-design/icons";
import moment from "moment";

const { Search } = Input;

function Logger() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const letRef = useRef(null);

  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleOk = () => {
    setIsModalOpen(false);
    setHandleViewState({ req: false, res: false, key: "" });
  };
  const handleCancel = () => {
    setIsModalOpen(false);
    setHandleViewState({ req: false, res: false, key: "" });
  };

  const dispatch = useDispatch();

  const { getLoggerData } = userActions;
  const { loggerData } = useSelector((state) => state.transactionReducer || {});

  const [searchRefId, setSearchRefId] = useState("");
  const [debouncedRefId, setDebouncedRefId] = useState(searchRefId);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [tableParams, setTableParams] = useState({
    current: 1,
    pageSize: 100,
  });

  // Debounce the searchRefId by 500ms
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedRefId(searchRefId);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchRefId]);

  useEffect(() => {
    dispatch(
      getLoggerData(
        tableParams.current, 
        tableParams.pageSize, 
        debouncedRefId,
        fromDate?.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
        toDate?.format("YYYY-MM-DDTHH:mm:ss.SSSZ")
      )
    );
  }, [tableParams.current, tableParams.pageSize, debouncedRefId, fromDate, toDate]);

  const [loggerDataState, setLoggerDataState] = useState({
    data: [],
    total: 0,
    returnTotal: 0,
  });

  const [handleViewState, setHandleViewState] = useState({
    req: false,
    res: false,
    reqData: {},
    resData: {},
  });

  const handleViewResponseDetails = (data, key) => {
    setHandleViewState({
      req: false,
      res: !handleViewState.res,
      key,
      resData: data,
    });
    letRef.current.click();
  };

  const handleViewRequestDetails = (data, key) => {
    setHandleViewState({
      req: !handleViewState.req,
      res: false,
      key,
      reqData: data,
    });
    letRef.current.click();
  };

  useEffect(() => {
    const dataSource = loggerData?.data?.logs?.map((item, index) => ({
      key: (tableParams.current - 1) * tableParams.pageSize + index + 1,
      url: item?.url,
      message: item?.message,
      responseTime: item?.responseTime,
      requestBody: item?.requestBody && (
        <span
          onClick={() => handleViewRequestDetails(item?.requestBody, index)}
          style={{
            display: "flex",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          {handleViewState.req && index === handleViewState.key ? (
            <RiCodeSLine size={25} />
          ) : (
            <RiCodeSSlashLine size={25} />
          )}
        </span>
      ),
      response: (item?.responseData || item?.error) && (
        <span
          onClick={() =>
            handleViewResponseDetails(item?.responseData || item?.error, index)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          {handleViewState.res && index === handleViewState.key ? (
            <RiCodeSLine size={25} />
          ) : (
            <RiCodeSSlashLine size={25} />
          )}
        </span>
      ),
      status: item?.error?.status || "",
      createdAt: item?.createdAt,
    }));

    setLoggerDataState({
      data: dataSource || [],
      total: loggerData?.data?.total || 0,
      returnTotal: loggerData?.data?.returnItems || 0,
    });
  }, [
    handleViewState.key,
    handleViewState.req,
    handleViewState.res,
    loggerData,
    tableParams.current,
    tableParams.pageSize,
  ]);

  const columns = [
    {
      title: "S. No.",
      dataIndex: "key",
      key: "key",
      width: 80,
    },
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      width: 200,
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      width: 150,
    },
    {
      title: "Response Time",
      dataIndex: "responseTime",
      key: "responseTime",
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
    },
    {
      title: "Request Body",
      dataIndex: "requestBody",
      key: "requestBody",
      width: 120,
    },
    {
      title: "Response",
      dataIndex: "response",
      key: "response",
      width: 120,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
    },
  ];

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setSearchRefId(value);
    setTableParams({ current: 1, pageSize: tableParams.pageSize });
  };

  const handlePageChange = (pagination) => {
    const { current, pageSize } = pagination;
    setTableParams({ current, pageSize });
  };

  const handleFromDateChange = (date) => {
    setFromDate(date);
    if (date > toDate) {
      setToDate(null);
    }
  };

  const handleToDateChange = (date) => {
    if (date?.isAfter(moment(), "day")) {
      setToDate(moment());
    } else if (date?.isSame(moment(), "day")) {
      if (
        date?.hour() > moment().hour() ||
        (date?.hour() === moment().hour() &&
          date?.minute() > moment().minute()) ||
        (date?.hour() === moment().hour() &&
          date?.minute() === moment().minute() &&
          date?.second() > moment().second())
      ) {
        setToDate(
          date
            .clone()
            .hour(moment().hour())
            .minute(moment().minute())
            .second(moment().second())
        );
      } else {
        setToDate(date);
      }
    } else {
      if (
        date?.isSame(fromDate, "day") &&
        date?.hour() === 0 &&
        date?.minute() === 0 &&
        date?.second() === 0
      ) {
        setToDate(date.clone().hour(23).minute(59).second(59));
      } else {
        setToDate(date);
      }
    }
    if (date && date < fromDate) {
      setFromDate(null);
    }
  };

  const disabledToDate = (current) => {
    return (
      (fromDate && current && current < fromDate?.startOf("day")) ||
      current > moment().endOf("day")
    );
  };

  const disabledDate = (current) => {
    return current && current > moment().endOf("day");
  };

  return (
    <>
      <div className="mainContain">
        <h1 className="transaction">Logger</h1>
        <div className="datesearchFi">
          <div className="">
            <div
              style={{
                width: "350px",
                outline: "none",
                border: "none",
                padding: "4px 8px",
                borderRadius: "4px",
              }}
            >
              <Input
                size="default size"
                placeholder="Search by refId"
                value={searchRefId}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="right-section">
            <div className="inputfatef">
              <div>
                <DatePicker
                  value={fromDate}
                  style={{ width: "100%", border: "none" }}
                  onChange={handleFromDateChange}
                  disabledDate={disabledDate}
                  placeholder="From(DD-MM-YYYY HH:mm:ss)"
                  showTime
                  format="DD-MM-YYYY HH:mm:ss"
                  suffixIcon={
                    <CalendarOutlined
                      style={{ color: "rgba(92, 26, 26, 0.25)" }}
                    />
                  }
                />
              </div>
              <div>
                <DatePicker
                  value={toDate}
                  style={{ width: "100%", border: "none" }}
                  onChange={handleToDateChange}
                  disabledDate={disabledToDate}
                  placeholder="To(DD-MM-YYYY HH:mm:ss)"
                  showTime={{
                    format: "HH:mm:ss",
                  }}
                  format="DD-MM-YYYY HH:mm:ss"
                  suffixIcon={
                    <CalendarOutlined
                      style={{ color: "rgba(92, 26, 26, 0.25)" }}
                    />
                  }
                />
              </div>
            </div>
          </div>
          <div style={{ display: "none" }}>
            <Button type="primary" onClick={showModal} ref={letRef}>
              Open Modal
            </Button>
          </div>
        </div>

        <div className="mt-3">
          <Table
            dataSource={loggerDataState.data}
            columns={columns}
            rowKey={(record) => record.key}
            scroll={{ x: 600 }}
            pagination={{
              total: loggerDataState.total,
              current: tableParams.current,
              pageSize: tableParams.pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ["50", "100", "200", "500"],
            }}
            onChange={handlePageChange}
          />
        </div>
      </div>

      <Modal
        title={handleViewState.req ? "Request" : "Response"}
        closable={{ "aria-label": "Custom Close Button" }}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <JSONTree
          data={
            (handleViewState.resData || handleViewState.reqData) !== ""
              ? handleViewState.resData || handleViewState.reqData
              : {}
          }
          theme={"bright:inverted"}
          shouldExpandNodeInitially={(index) => true}
        />
      </Modal>
    </>
  );
}

export default Logger;
